import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function formatVoiceDuration(secondsOrMs) {
  const raw = Number(secondsOrMs || 0);
  if (!Number.isFinite(raw) || raw <= 0) return "0:00";
  const seconds = raw > 1000 ? Math.round(raw / 1000) : Math.round(raw);
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${`${remainder}`.padStart(2, "0")}`;
}

export function getVoiceNoteAttachment(message) {
  if (!Array.isArray(message?.attachments)) return null;
  return (
    message.attachments.find((attachment) => attachment?.type === "voice_note") ||
    null
  );
}

export function isVoiceNoteMessage(message) {
  return message?.type === "voice_note" || Boolean(getVoiceNoteAttachment(message));
}

export default function VoiceNotePlayer({
  src,
  durationMs = 0,
  isMine = false,
  label = "Voice note",
  compact = false,
}) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [resolvedDuration, setResolvedDuration] = useState(durationMs > 0 ? durationMs / 1000 : 0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const handleLoadedMetadata = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setResolvedDuration(audio.duration);
      }
    };
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime || 0);
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.pause();
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [src]);

  const effectiveDuration = useMemo(() => {
    if (resolvedDuration > 0) return resolvedDuration;
    if (durationMs > 0) return durationMs / 1000;
    return 0;
  }, [durationMs, resolvedDuration]);

  const progressPercent = useMemo(() => {
    if (!effectiveDuration) return 0;
    return clamp((currentTime / effectiveDuration) * 100, 0, 100);
  }, [currentTime, effectiveDuration]);

  const togglePlayback = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      return;
    }

    try {
      await audio.play();
    } catch (_) {
      setIsPlaying(false);
    }
  }, [isPlaying]);

  const scrubTo = useCallback(
    (event) => {
      const audio = audioRef.current;
      if (!audio || !effectiveDuration) return;

      const rect = event.currentTarget.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      const nextTime = effectiveDuration * ratio;
      audio.currentTime = nextTime;
      setCurrentTime(nextTime);
    },
    [effectiveDuration],
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: compact ? 8 : 10,
        width: "100%",
        minWidth: compact ? 180 : 220,
        padding: compact ? "8px 10px" : "10px 12px",
        borderRadius: 18,
        background: isMine ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.07)",
      }}
    >
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        type="button"
        onClick={togglePlayback}
        title={isPlaying ? "Pause voice note" : "Play voice note"}
        aria-label={isPlaying ? "Pause voice note" : "Play voice note"}
        style={{
          width: compact ? 34 : 38,
          height: compact ? 34 : 38,
          borderRadius: "50%",
          border: "none",
          flexShrink: 0,
          cursor: "pointer",
          background: isMine ? "#ffffff" : "#1876f2",
          color: isMine ? "#1876f2" : "#ffffff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 1px 4px rgba(0,0,0,0.16)",
        }}
      >
        <i className={`fa-solid ${isPlaying ? "fa-pause" : "fa-play"}`} />
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          onClick={scrubTo}
          style={{
            width: "100%",
            height: 8,
            borderRadius: 999,
            cursor: "pointer",
            background: isMine ? "rgba(255,255,255,0.28)" : "rgba(24,118,242,0.15)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progressPercent}%`,
              height: "100%",
              borderRadius: 999,
              background: isMine ? "#ffffff" : "#1876f2",
              transition: isPlaying ? "width 0.1s linear" : "width 0.15s ease",
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginTop: 6,
            fontSize: 11,
            opacity: isMine ? 0.92 : 0.8,
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {label}
          </span>
          <span>{formatVoiceDuration(isPlaying ? currentTime : effectiveDuration)}</span>
        </div>
      </div>
    </div>
  );
}
